import React, { useState } from 'react';
import { useAppContext } from '../App';
import { sanitizeInput, isValidEmail } from '../utils/security';

const ContactPage = () => {
  const { showNotification } = useAppContext();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'supplier', label: 'Become a Supplier' },
    { value: 'bulk', label: 'Bulk Order Request' },
    { value: 'partnership', label: 'Partnership Opportunity' },
    { value: 'support', label: 'Customer Support' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    showNotification('Message sent successfully! We\'ll get back to you soon.', 'success');

    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: '',
      inquiryType: 'general'
    });

    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
      title: 'Email',
      value: 'contact@asiaquimicos.com',
      link: 'mailto:contact@asiaquimicos.com'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 2 2 0 0 1-2.18-1.18A11.72 11.72 0 0 1-6.52-2.36 2 2 0 0 1-.96-3.72 12 12 0 0 1 3.72-6.52 2 2 0 0 1 3.72-.96A11.72 11.72 0 0 1 6.52-2.36c1.16-.36 2.84.06 3.72 1.08a2 2 0 0 1 2.18 2z" />
        </svg>
      ),
      title: 'Phone',
      value: '+1 (555) 123-4567',
      link: 'tel:+15551234567'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        </svg>
      ),
      title: 'Location',
      value: 'Singapore, Asia Pacific',
      link: null
    }
  ];

  const faqs = [
    {
      q: 'How do I become a verified supplier?',
      a: 'Apply through our supplier portal. Our team will review your application and credentials within 5 business days.'
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept wire transfers, credit cards, and letters of credit. Payment terms can be negotiated with suppliers.'
    },
    {
      q: 'Is there a minimum order quantity?',
      a: 'Minimum order quantities vary by supplier and product. You can find MOQ details on each product page.'
    },
    {
      q: 'How do you verify suppliers?',
      a: 'We verify business licenses, certifications (ISO, REACH, GMP), and conduct background checks on all suppliers.'
    }
  ];

  return (
    <div className="contact-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">Get in touch with our team for any questions or inquiries</p>
        </div>

        <div className="contact-grid">
          {/* Contact Form */}
          <div className="contact-form-container">
            <h2>Send us a message</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={errors.name ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className={errors.email ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Name"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 234 567 890"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="inquiryType">Inquiry Type</label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  {inquiryTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  className={errors.subject ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your inquiry..."
                  rows={5}
                  className={errors.message ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button
                type="submit"
                className="btn btn-primary submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="contact-info-container">
            <h2>Get in touch</h2>
            <p className="info-subtitle">We're here to help and answer any questions you might have</p>

            <div className="contact-cards">
              {contactInfo.map((info, i) => (
                <div key={i} className="contact-card">
                  <div className="card-icon">{info.icon}</div>
                  <div className="card-content">
                    <h4>{info.title}</h4>
                    {info.link ? (
                      <a href={info.link}>{info.value}</a>
                    ) : (
                      <span>{info.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Business Hours */}
            <div className="business-hours">
              <h3>Business Hours</h3>
              <div className="hours-grid">
                <div className="hours-row">
                  <span>Monday - Friday</span>
                  <span>9:00 AM - 6:00 PM (SGT)</span>
                </div>
                <div className="hours-row">
                  <span>Saturday</span>
                  <span>10:00 AM - 2:00 PM (SGT)</span>
                </div>
                <div className="hours-row">
                  <span>Sunday</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <h4>{faq.q}</h4>
                <p>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .contact-page {
          padding: var(--space-xl) 0 var(--space-3xl);
          min-height: 100vh;
        }

        .page-header {
          text-align: center;
          margin-bottom: var(--space-2xl);
        }

        .page-title {
          font-size: var(--font-4xl);
          font-weight: 700;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-sm);
        }

        .page-subtitle {
          font-size: var(--font-lg);
          color: var(--color-neutral-400);
        }

        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-2xl);
          margin-bottom: var(--space-3xl);
        }

        .contact-form-container,
        .contact-info-container {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-xl);
          padding: var(--space-xl);
        }

        .contact-form-container h2,
        .contact-info-container h2 {
          font-size: var(--font-xl);
          color: var(--color-neutral-50);
          margin-bottom: var(--space-lg);
        }

        .info-subtitle {
          color: var(--color-neutral-400);
          margin-bottom: var(--space-lg);
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-md);
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }

        .form-group label {
          font-size: var(--font-sm);
          font-weight: 500;
          color: var(--color-neutral-300);
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: var(--space-sm) var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: var(--radius-md);
          color: var(--color-neutral-100);
          font-size: var(--font-sm);
          transition: all var(--transition-fast);
          resize: vertical;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--color-accent-400);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .form-group input.error,
        .form-group textarea.error {
          border-color: var(--color-error);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: var(--color-neutral-500);
        }

        .error-message {
          font-size: var(--font-xs);
          color: var(--color-error);
        }

        .submit-btn {
          margin-top: var(--space-sm);
          padding: var(--space-md);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-sm);
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .contact-cards {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
          margin-bottom: var(--space-xl);
        }

        .contact-card {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          padding: var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border-radius: var(--radius-md);
        }

        .card-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(59, 130, 246, 0.2);
          border-radius: var(--radius-md);
          color: var(--color-accent-400);
        }

        .card-content h4 {
          font-size: var(--font-sm);
          color: var(--color-neutral-400);
          font-weight: 400;
          margin-bottom: 2px;
        }

        .card-content a,
        .card-content span {
          color: var(--color-neutral-100);
          font-size: var(--font-base);
        }

        .card-content a:hover {
          color: var(--color-accent-400);
        }

        .business-hours {
          padding: var(--space-lg);
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-md);
        }

        .business-hours h3 {
          font-size: var(--font-base);
          color: var(--color-neutral-200);
          margin-bottom: var(--space-md);
        }

        .hours-grid {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .hours-row {
          display: flex;
          justify-content: space-between;
          font-size: var(--font-sm);
        }

        .hours-row span:first-child {
          color: var(--color-neutral-400);
        }

        .hours-row span:last-child {
          color: var(--color-neutral-200);
        }

        .faq-section {
          text-align: center;
        }

        .faq-section h2 {
          font-size: var(--font-2xl);
          color: var(--color-neutral-50);
          margin-bottom: var(--space-xl);
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-lg);
          text-align: left;
        }

        .faq-item {
          padding: var(--space-lg);
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
        }

        .faq-item h4 {
          font-size: var(--font-base);
          color: var(--color-neutral-100);
          margin-bottom: var(--space-sm);
        }

        .faq-item p {
          font-size: var(--font-sm);
          color: var(--color-neutral-400);
          line-height: 1.6;
        }

        @media (max-width: 1024px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }

          .faq-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
